/**
 * 【SAD0013】受注情報管理・オプション編集 / クライアントサイド再計算。
 * 本画面に x-file-manager は無い。`file_status` 定義は `resources/js/enums/file-manager-file-status.js` を参照。
 *
 * - 各オプション行: 数量変更で 小計（=単価×数量）と 原価計（=原価×数量）を更新。
 * - フッター「オプション・追加金額合計」（単価／原価）= オプション表の小計（単価×数量）+ 追加金額表の入力行。
 *
 * 単価・原価は読み取り専用（dataset 経由）として扱い、原本データを保持する。
 */

import { initQuantitySteppers } from '../share/quantity-stepper';

const ROOT_SELECTOR = '#admin-orders-options-edit-root';

/**
 * 数値以外の入力を 0 に丸める。
 *
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
function ordersOptionsEditParseNumber(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return 0;
    }
    return parsed;
}

/**
 * 数値を 3 桁区切り + 円表記に整形する。
 *
 * @param {number} value
 * @param {HTMLElement} root
 * @returns {string}
 */
function ordersOptionsEditFormatYen(value, root) {
    const suffix = (root.dataset.yenSuffix ?? '').trim();
    const formatted = `${Math.round(value).toLocaleString('ja-JP')}`;
    return suffix === '' ? formatted : `${formatted} ${suffix}`;
}

/**
 * 1 行の小計と原価計を更新し、合計値を返す。
 *
 * @param {HTMLElement} row
 * @param {HTMLElement} root
 * @returns {{ subtotal: number, costSubtotal: number }}
 */
function ordersOptionsEditRecalcRow(row, root) {
    const unitCell = row.querySelector('.orders-options-edit-row__unit-price');
    const costCell = row.querySelector('.orders-options-edit-row__cost');
    const quantityInput = row.querySelector('.orders-options-edit-row__quantity');
    const subtotalCell = row.querySelector('.orders-options-edit-row__subtotal');
    const costSubtotalCell = row.querySelector('.orders-options-edit-row__cost-subtotal');
    const unitPrice = ordersOptionsEditParseNumber(unitCell?.dataset.unitPrice);
    const cost = ordersOptionsEditParseNumber(costCell?.dataset.cost);
    const quantity = ordersOptionsEditParseNumber(quantityInput?.value);
    const subtotal = unitPrice * quantity;
    const costSubtotal = cost * quantity;
    if (subtotalCell) {
        subtotalCell.textContent = ordersOptionsEditFormatYen(subtotal, root);
    }
    if (costSubtotalCell) {
        costSubtotalCell.textContent = ordersOptionsEditFormatYen(costSubtotal, root);
    }
    return { subtotal, costSubtotal };
}

/**
 * 追加金額 1 行から単価・原価を取得（合計用）。
 *
 * @param {HTMLElement} row
 * @returns {{ unit: number, cost: number }}
 */
function ordersOptionsEditReadAddRow(row) {
    const unitInput = row.querySelector('.orders-options-edit-add-row__unit-price');
    const costInput = row.querySelector('.orders-options-edit-add-row__cost');
    return {
        unit: ordersOptionsEditParseNumber(unitInput?.value),
        cost: ordersOptionsEditParseNumber(costInput?.value),
    };
}

/**
 * 全行を再計算し、合計セルを更新する。
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function ordersOptionsEditRecalcAll(root) {
    let optionsUnitTotal = 0;
    let optionsCostTotal = 0;
    root.querySelectorAll('.orders-options-edit-row:not(.orders-options-edit-row--catalog-info)').forEach((row) => {
        const { subtotal, costSubtotal } = ordersOptionsEditRecalcRow(row, root);
        optionsUnitTotal += subtotal;
        optionsCostTotal += costSubtotal;
    });
    let additionalUnitTotal = 0;
    let additionalCostTotal = 0;
    root.querySelectorAll('.orders-options-edit-add-row').forEach((row) => {
        const { unit, cost } = ordersOptionsEditReadAddRow(row);
        additionalUnitTotal += unit;
        additionalCostTotal += cost;
    });
    const combinedUnitTotal = optionsUnitTotal + additionalUnitTotal;
    const combinedCostTotal = optionsCostTotal + additionalCostTotal;
    const totalUnitCell = root.querySelector('.orders-options-edit-total__unit-price');
    const totalCostCell = root.querySelector('.orders-options-edit-total__cost');
    if (totalUnitCell) {
        totalUnitCell.textContent = ordersOptionsEditFormatYen(combinedUnitTotal, root);
    }
    if (totalCostCell) {
        totalCostCell.textContent = ordersOptionsEditFormatYen(combinedCostTotal, root);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) {
        return;
    }

    initQuantitySteppers(root, {
        onChange: () => {
            ordersOptionsEditRecalcAll(root);
        },
    });

    ordersOptionsEditRecalcAll(root);

    root.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        if (
            target.classList.contains('orders-options-edit-add-row__unit-price') ||
            target.classList.contains('orders-options-edit-add-row__cost')
        ) {
            ordersOptionsEditRecalcAll(root);
        }
    });
});
